import React, { useCallback, useState, useRef } from "react";
import { Button, Card, CardBody, CardHeader, CardTitle, Row, Col, Badge } from "reactstrap";
import apiGd from "services/apiGd";
import { endOfDay, startOfDay, differenceInDays } from "date-fns";
import { FullHourCalendar, InformationDiv } from "ui-kit-takeat";
import formatPrice from "services/formatPrice";
import formatCompleteDate from "services/formatCompleteDate";
import { OrderGroup, OrderCard, OrderHeader, OrderInfo, InfoRow, InfoLabel, InfoValue, CancelButton, CheckboxContainer, FloatingCounter, CounterNumber, CounterLabel, CounterButton } from "./styles";
import NotificationAlert from "react-notification-alert";

export default function DuplicatedIfoodOrders() {
  const token = localStorage.getItem("@QRExpress:tokenGd");
  const notificationAlert = useRef(null);
  const notify = (place, color, message) => {
    var options = {};
    options = {
    place: place,
    message: (
        <div>
        <div>{message}</div>
        </div>
    ),
    type: color,
    icon: "now-ui-icons ui-1_bell-53",
    autoDismiss: 7,
    };
    notificationAlert.current.notificationAlert(options);
  };

  const [filterDateStart, setFilterDateStart] = useState(startOfDay(new Date()));
  const [filterDateEnd, setFilterDateEnd] = useState(endOfDay(new Date()));
  const [duplicatedOrders, setDuplicatedOrders] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [cancellingBatch, setCancellingBatch] = useState(false);
  const [cancellingIndividual, setCancellingIndividual] = useState(false);

  const handleSearch = useCallback(async () => {
    const daysDiff = differenceInDays(filterDateEnd, filterDateStart);
    
    if (daysDiff > 2) {
      notify('tr', 'warning', 'O intervalo entre as datas não pode ser maior que 2 dias');
      return;
    }

    // Limpa os pedidos selecionados ao fazer nova busca
    setSelectedOrders([]);
    
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await apiGd.get('admin/restaurants/orders/duplicated-ifood', {
        params: {
          start_date: filterDateStart.toISOString(),
          end_date: filterDateEnd.toISOString(),
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      setDuplicatedOrders(response.data);
      
      const totalGroups = Object.keys(response.data).length;
      const totalOrders = Object.values(response.data).reduce((acc, orders) => acc + orders.length, 0);
    
      if (totalGroups === 0) return;
      notify('tr', 'success', `Encontrados ${totalGroups} grupos com ${totalOrders} pedidos duplicados`);
    } catch (err) {
      console.log(err);
      notify('tr', 'danger', err?.response?.data?.message || 'Erro ao buscar pedidos duplicados');
    } finally {
      setLoading(false);
    }
  }, [token, filterDateEnd, filterDateStart, notify]);

  const handleCancelOrder = useCallback(async (orderId, ifoodId) => {
    if (!window.confirm(`Tem certeza que deseja cancelar o pedido #${orderId}?`)) {
      return;
    }

    setCancellingIndividual(true);
    try {
      await apiGd.delete(`admin/restaurants/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Remove o pedido selecionado se existir
      setSelectedOrders(prev => prev.filter(id => id !== orderId));

      // Remove o pedido da lista
      setDuplicatedOrders(prev => {
        const newData = { ...prev };
        newData[ifoodId] = newData[ifoodId].filter(order => order.id !== orderId);
        
        // Remove o grupo se houver apenas 1 pedido restante (não é mais duplicado)
        if (newData[ifoodId].length <= 1) {
          delete newData[ifoodId];
        }
        
        return newData;
      });

      notify('tr', 'success', 'Pedido cancelado com sucesso');
    } catch (err) {
      console.log(err);
      notify('tr', 'danger', err?.response?.data?.message || 'Erro ao cancelar pedido');
    } finally {
      setCancellingIndividual(false);
    }
  }, [token, notify]);

  // Verifica se está cancelando (individual ou em massa)
  const isCancelling = cancellingBatch || cancellingIndividual;

  const handleToggleSelect = useCallback((orderId, ifoodId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        // Desmarca o pedido
        return prev.filter(id => id !== orderId);
      } else {
        // Verifica se já atingiu o limite de 20
        if (prev.length >= 20) {
          notify('tr', 'warning', 'Você pode selecionar no máximo 20 pedidos');
          return prev;
        }
        // Marca o pedido
        return [...prev, orderId];
      }
    });
  }, [notify]);

  const getSelectedCountInGroup = useCallback((ifoodId) => {
    const groupOrders = duplicatedOrders[ifoodId] || [];
    return groupOrders.filter(order => selectedOrders.includes(order.id)).length;
  }, [duplicatedOrders, selectedOrders]);

  const handleCheckboxClick = useCallback((orderId, ifoodId, isDisabled) => {
    if (isDisabled && !selectedOrders.includes(orderId)) {
      if (selectedOrders.length >= 20) {
        notify('tr', 'warning', 'Limite de 20 pedidos atingido');
      } else {
        notify('tr', 'info', 'Você deve deixar pelo menos 1 pedido sem selecionar em cada grupo');
      }
    }
  }, [selectedOrders, notify]);

  const isCheckboxDisabled = useCallback((orderId, ifoodId) => {
    // Se já está selecionado, pode desmarcar
    if (selectedOrders.includes(orderId)) {
      return false;
    }
    
    // Se já tem 20 selecionados no total, desabilita
    if (selectedOrders.length >= 20) {
      return true;
    }

    const groupOrders = duplicatedOrders[ifoodId] || [];
    const selectedInGroup = getSelectedCountInGroup(ifoodId);
    
    // Desabilita se já selecionou N-1 pedidos do grupo (deixa sempre 1 sem selecionar)
    return selectedInGroup >= groupOrders.length - 1;
  }, [selectedOrders, duplicatedOrders, getSelectedCountInGroup]);

  const handleAutoSelect = useCallback(() => {
    // Limpa seleção atual
    setSelectedOrders([]);

    // Array para armazenar todos os pedidos duplicados encontrados
    const duplicatesToSelect = [];

    // Percorre todos os grupos de pedidos
    Object.entries(duplicatedOrders).forEach(([ifoodId, orders]) => {
      // Agrupa pedidos por combinação de total_paid e nfce_allowed
      const groupedByAttributes = {};
      
      orders.forEach(order => {
        const key = `${order.total_paid}_${order.nfce_allowed}`;
        if (!groupedByAttributes[key]) {
          groupedByAttributes[key] = [];
        }
        groupedByAttributes[key].push(order);
      });

      // Para cada grupo de atributos, seleciona os duplicados (exceto o de menor ID)
      Object.values(groupedByAttributes).forEach(group => {
        if (group.length > 1) {
          // Ordena por ID (maior primeiro)
          const sortedGroup = [...group].sort((a, b) => b.id - a.id);
          
          // Seleciona todos exceto o último (menor ID)
          // Isso garante que sempre sobra pelo menos 1 pedido sem selecionar
          const toSelect = sortedGroup.slice(0, -1);
          duplicatesToSelect.push(...toSelect);
        }
      });
    });

    // Limita a 20 pedidos
    const limitedSelection = duplicatesToSelect.slice(0, 20);
    const orderIds = limitedSelection.map(order => order.id);
    
    setSelectedOrders(orderIds);
    
    if (limitedSelection.length > 0) {
      notify('tr', 'success', `${limitedSelection.length} pedido(s) selecionado(s) automaticamente`);
    } else {
      notify('tr', 'info', 'Nenhum pedido duplicado encontrado para seleção automática');
    }
  }, [duplicatedOrders, notify]);

  const handleCancelBatch = useCallback(async () => {
    if (selectedOrders.length === 0) {
      notify('tr', 'warning', 'Nenhum pedido selecionado');
      return;
    }

    if (!window.confirm(`Tem certeza que deseja cancelar ${selectedOrders.length} pedido(s)?`)) {
      return;
    }

    console.log('=== CANCELAMENTO EM MASSA ===');
    console.log('IDs selecionados:', selectedOrders);
    console.log('Payload que será enviado:', { array: selectedOrders });
    console.log('============================');

    setCancellingBatch(true);
    try {
      await apiGd.put('admin/restaurants/delivery/orders/cancel', {
        array: selectedOrders
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Remove os pedidos cancelados da lista
      setDuplicatedOrders(prev => {
        const newData = { ...prev };
        
        Object.keys(newData).forEach(ifoodId => {
          newData[ifoodId] = newData[ifoodId].filter(order => !selectedOrders.includes(order.id));
          
          // Remove o grupo se houver apenas 1 pedido restante (não é mais duplicado)
          if (newData[ifoodId].length <= 1) {
            delete newData[ifoodId];
          }
        });
        
        return newData;
      });

      notify('tr', 'success', `${selectedOrders.length} pedido(s) cancelado(s) com sucesso`);
      setSelectedOrders([]);
    } catch (err) {
      console.log(err);
      notify('tr', 'danger', err?.response?.data?.message || 'Erro ao cancelar pedidos');
    } finally {
      setCancellingBatch(false);
    }
  }, [selectedOrders, token, notify]);

  const getStatusColor = (status) => {
    const colors = {
      open: 'warning',
      finished: 'secondary',
      canceled: 'danger',
      completed: 'success'
    };
    return colors[status] || 'info';
  };

  const getStatusLabel = (status) => {
    const labels = {
      open: 'Aberto',
      finished: 'Fechado',
      canceled: 'Cancelado',
      completed: 'Finalizado'
    };
    return labels[status] || status;
  };

  return (
    <>
    <NotificationAlert ref={notificationAlert} />
    
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Pedidos Duplicados de Integrações</CardTitle>
            </CardHeader>
            <CardBody>
              <InformationDiv
                text={
                  <>
                    Selecione o período para buscar pedidos de integração <b>duplicados</b> <br />
                    O intervalo máximo permitido é de <b>2 dias</b> <br />
                  </>
                }
                type='info'
              />
              
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 20, marginBottom: 20, justifyContent: 'flex-end' }}>
                <FullHourCalendar
                  value={{ start: filterDateStart, end: filterDateEnd }}
                  onDateSelected={(e) => {
                    setFilterDateStart(e.start);
                  }}
                />
                <FullHourCalendar
                  isEnd
                  value={{ start: filterDateStart, end: filterDateEnd }}
                  onDateSelected={(e) => {
                    setFilterDateEnd(e.end);
                  }}
                />
                <Button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading}
                  style={{ backgroundColor: "#2ec9b7", borderRadius: 8 }}
                >
                  {loading ? 'Buscando...' : 'Buscar Pedidos'}
                </Button>
              </div>

              {Object.keys(duplicatedOrders).length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <Button
                    type="button"
                    onClick={handleAutoSelect}
                    disabled={loading || isCancelling}
                    color="info"
                    style={{ borderRadius: 8 }}
                  >
                    Selecionar Automaticamente (20 primeiros)
                  </Button>
                  <small style={{ marginLeft: 10, color: '#666' }}>
                    Seleciona automaticamente os pedidos duplicados (mesmo ID de integração, total pago e situação da nota fiscal), mantendo sempre o primeiro pedido realizado
                  </small>
                </div>
              )}

              {Object.keys(duplicatedOrders).length === 0 && !loading && hasSearched && (
                <InformationDiv
                  text="Nenhum pedido duplicado encontrado no período selecionado"
                  type='warning'
                />
              )}

              {Object.entries(duplicatedOrders).map(([ifoodId, orders]) => (
                <OrderGroup key={ifoodId}>
                  <h5 style={{ marginBottom: 15, color: '#51cbce' }}>
                    ID de integração: <strong>{ifoodId}</strong> 
                    <Badge color="danger" style={{ marginLeft: 10 }}>
                      {orders.length} pedidos duplicados
                    </Badge>
                  </h5>
                  
                  <Row>
                    {orders.map((order) => (
                      <Col md="6" lg="4" key={order.id}>
                        <OrderCard>
                          <CheckboxContainer 
                            onClick={() => handleCheckboxClick(order.id, ifoodId, isCheckboxDisabled(order.id, ifoodId) || order.status === 'canceled')}
                          >
                            <input
                              type="checkbox"
                              checked={selectedOrders.includes(order.id)}
                              onChange={() => handleToggleSelect(order.id, ifoodId)}
                              disabled={isCheckboxDisabled(order.id, ifoodId) || order.status === 'canceled'}
                              title={
                                order.status === 'canceled' 
                                  ? 'Pedido já cancelado' 
                                  : isCheckboxDisabled(order.id, ifoodId) && !selectedOrders.includes(order.id)
                                  ? 'Limite atingido'
                                  : 'Selecionar pedido'
                              }
                            />
                          </CheckboxContainer>
                          
                          <OrderHeader>
                            <div>
                              <h6>Pedido #{order.attendance_password}</h6>
                              <small>ID interno: #{order.id}</small>
                            </div>
                            <Badge color={getStatusColor(order.status)}>
                              {getStatusLabel(order.status)}
                            </Badge>
                          </OrderHeader>

                          <OrderInfo>
                            <InfoRow>
                              <InfoLabel>Restaurante:</InfoLabel>
                              <InfoValue>{order.restaurant.fantasy_name}</InfoValue>
                            </InfoRow>

                            <InfoRow>
                              <InfoLabel>Nome:</InfoLabel>
                              <InfoValue>{order.restaurant.name}</InfoValue>
                            </InfoRow>

                            <InfoRow>
                              <InfoLabel>Canal:</InfoLabel>
                              <InfoValue>
                                <Badge color="primary">{order.sales_channel}</Badge>
                              </InfoValue>
                            </InfoRow>

                            <InfoRow>
                              <InfoLabel>Total Pago:</InfoLabel>
                              <InfoValue style={{ fontWeight: 'bold', color: '#51cbce' }}>
                                {formatPrice(order.total_paid)}
                              </InfoValue>
                            </InfoRow>

                            <InfoRow>
                              <InfoLabel>Nº Pagamentos:</InfoLabel>
                              <InfoValue>{order.payments.length}</InfoValue>
                            </InfoRow>

                            <InfoRow>
                              <InfoLabel>Valor dos produtos:</InfoLabel>
                              <InfoValue style={{ fontWeight: 'bold', color: '#51cbce' }}>
                                {formatPrice(order.total_price || 0)}
                              </InfoValue>
                            </InfoRow>

                            <InfoRow>
                              <InfoLabel>Valor total:</InfoLabel>
                              <InfoValue style={{ fontWeight: 'bold', color: '#51cbce' }}>
                                {formatPrice(order.total_delivery_price || 0)}
                              </InfoValue>
                            </InfoRow>

                            <InfoRow>
                              <InfoLabel>NFCe:</InfoLabel>
                              <InfoValue>
                                <InfoValue>
                                  {console.log(order.nfce_allowed)}
                                  {order.nfce_allowed ? 'Emitida' : 'Não emitida'}
                                </InfoValue>
                              </InfoValue>
                            </InfoRow>
                            
                            <InfoRow>
                              <InfoLabel>Criado em:</InfoLabel>
                              <InfoValue>{formatCompleteDate(order.created_at)}</InfoValue>
                            </InfoRow>
                            
                            <InfoRow>
                              <InfoLabel>Fim:</InfoLabel>
                              <InfoValue>{order.end_time ? formatCompleteDate(order.end_time) : 'Em aberto'}</InfoValue>
                            </InfoRow>
                          </OrderInfo>

                          {/* <CancelButton
                            onClick={() => handleCancelOrder(order.id, ifoodId)}
                            disabled={order.status === 'canceled' || isCancelling}
                          >
                            {order.status === 'canceled' ? 'Cancelado' : isCancelling ? 'Aguarde...' : 'Cancelar Pedido'}
                          </CancelButton> */}
                        </OrderCard>
                      </Col>
                    ))}
                  </Row>
                </OrderGroup>
              ))}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>

    {selectedOrders.length > 0 && (
      <FloatingCounter>
        <CounterNumber>{selectedOrders.length}</CounterNumber>
        <CounterLabel>
          {selectedOrders.length === 1 ? 'pedido selecionado' : 'pedidos selecionados'}
          <br />
          <small>(máx. 20)</small>
        </CounterLabel>
        <CounterButton
          onClick={handleCancelBatch}
          disabled={isCancelling}
        >
          {isCancelling ? 'Cancelando...' : 'Cancelar Selecionados'}
        </CounterButton>
      </FloatingCounter>
    )}
    </>
  );
}
